import { PropsWithChildren } from "react";

import { useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import { useLocalizedHref } from "@/libraries/web-framework";
import {
    TypographyStyles,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src";

import { TitleCard, TitleCardProps } from "./components/card";
import { MotionContainer } from "./components/motion-container";
import { RowGrid } from "./layouts/row-grid";
import { useGlobalNavContext, DivOnHoverExit } from "./nav-links-container";

export function useWhyAtlassianData(): TitleCardProps[] {
    const intl = useIntl();

    return [
        {
            title: intl.formatMessage({
                id: "globalnavv2.subnav.whyatlassian.systemofwork.title",
                defaultMessage: "System of Work"
            }),
            content: intl.formatMessage({
                id: "globalnavv2.subnav.whyatlassian.systemofwork.content",
                defaultMessage:
                    "Atlassian's blueprint for how teams work together"
            }),
            href: useLocalizedHref("/system-of-work"),
            isNew: true
        },
        {
            title: intl.formatMessage({
                id: "globalnavv2.subnav.whyatlassian.integrations.title",
                defaultMessage: "Marketplace"
            }),
            content: intl.formatMessage({
                id: "globalnavv2.subnav.whyatlassian.integrations.content",
                defaultMessage:
                    "Connect thousands of apps to your Atlassian products"
            }),
            href: "//marketplace.atlassian.com/"
        },
        {
            title: intl.formatMessage({
                id: "globalnavv2.subnav.whyatlassian.customers.title",
                defaultMessage: "Customers"
            }),
            content: intl.formatMessage({
                id: "globalnavv2.subnav.whyatlassian.customers.content",
                defaultMessage: "Case studies & stories powered by teamwork"
            }),
            href: useLocalizedHref("/customers")
        },
        {
            title: "FedRAMP",
            content: intl.formatMessage({
                id: "globalnavv2.subnav.whyatlassian.fedramp.content",
                defaultMessage: "Compliant solutions for the public sector"
            }),
            href: useLocalizedHref("/trust/compliance/resources/fedramp")
        },
        {
            title: intl.formatMessage({
                id: "globalnavv2.subnav.whyatlassian.resilience.title",
                defaultMessage: "Resilience"
            }),
            content: intl.formatMessage({
                id: "globalnavv2.subnav.whyatlassian.resilience.content",
                defaultMessage:
                    "Enterprise-grade & highly performant infrastructure"
            }),
            href: useLocalizedHref("/trust/resilience")
        },
        {
            title: intl.formatMessage({
                id: "globalnavv2.subnav.whyatlassian.platform.title",
                defaultMessage: "Platform"
            }),
            content: intl.formatMessage({
                id: "globalnavv2.subnav.whyatlassian.platform.content",
                defaultMessage:
                    "Our deeply integrated, reliable & secure platform"
            }),
            href: useLocalizedHref("/platform")
        },
        {
            title: intl.formatMessage({
                id: "globalnavv2.subnav.whyatlassian.trustcenter.title",
                defaultMessage: "Trust center"
            }),
            content: intl.formatMessage({
                id: "globalnavv2.subnav.whyatlassian.trustcenter.content",
                defaultMessage:
                    "Ensure your data's security, compliance & availability"
            }),
            href: useLocalizedHref("/trust")
        }
    ];
}

const WhyAtlassianGrid = ({ children }: PropsWithChildren) => {
    return (
        <RowGrid
            numCols={4}
            rowGap={token("space.500")}
            columnGap={token("space.400")}
            paddingBlock={token("space.500")}
            paddingInline={token("space.500")}
        >
            {children}
        </RowGrid>
    );
};

export const WhyAtlassianSubnav = ({
    productKey = "home"
}: {
    productKey?: ProductKeyTypes;
}) => {
    const teamsData = useWhyAtlassianData();
    const { direction } = useGlobalNavContext();

    return (
        <DivOnHoverExit>
            <TypographyStyles>
                <MotionContainer direction={direction}>
                    <WhyAtlassianGrid>
                        {teamsData.map(({ title, ...rest }) => (
                            <TitleCard
                                key={title}
                                title={title}
                                {...rest}
                                productKey={productKey}
                            />
                        ))}
                    </WhyAtlassianGrid>
                </MotionContainer>
            </TypographyStyles>
        </DivOnHoverExit>
    );
};
